import { formatFibreSummary } from "./fibreCompositionData";
import {
  MAXIMUM_LIQUID_TEMPERATURE,
  OTHER_WARNING,
  hasRestriction,
} from "./safetyData";
import {
  isGpsrClothingTextileProductType,
  mapGpsrFibreComponentsFromApi,
  mapProductLabelDetailToGpsrForm,
  resolveGpsrProductLabelDigitalIdentity,
  resolveGpsrProductLabelFilePaths,
} from "./gpsrLabelMappers";
import {
  FALLBACK_CARE_SYMBOL_OPTIONS,
  FALLBACK_FIBRE_COMPONENT_TYPE_OPTIONS,
  FALLBACK_FIBRE_TYPE_OPTIONS,
  FALLBACK_MINIMUM_USER_AGE_OPTIONS,
  FALLBACK_PRODUCT_CATEGORY_OPTIONS,
  FALLBACK_USE_RESTRICTION_OPTIONS,
  FALLBACK_WEIGHT_UNIT_OPTIONS,
  buildGpsrRepresentativeTypeOptions,
  pickGpsrSelectOptions,
  resolveGpsrOptionLabel,
} from "./gpsrSelectOptions";
import buildInvoicePreviewUrl from "helpers/buildInvoicePreviewUrl";
import { formatPpwrWeightValue } from "../CreatePpwrLabelPage/packagingCompositionData";

const displayText = (value) => String(value ?? "").trim();

const formatPartyAddress = (party = {}, resolveCountryLabel) => {
  const countryLabel =
    typeof resolveCountryLabel === "function"
      ? resolveCountryLabel(party?.country)
      : displayText(party?.country);
  return [
    displayText(party?.streetAddress),
    [displayText(party?.postalCode), displayText(party?.city)]
      .filter(Boolean)
      .join(" "),
    countryLabel,
  ]
    .filter(Boolean)
    .join(", ");
};

export const buildGpsrPreviewDigitalUrl = (productId) => {
  if (typeof window === "undefined" || !window.location?.origin) return "";
  const id = displayText(productId);
  const url = new URL(
    "/products/gpsr-label/preview",
    window.location.origin
  );
  if (id) url.searchParams.set("productId", id);
  return url.toString();
};

export const buildGpsrPrintableLabelProps = ({
  traceability = {},
  manufacturer = {},
  representative = {},
  importer = {},
  fibreComposition = {},
  safety = {},
  resolveCountryLabel,
  qrCodeValue = null,
  qrCodeImage = null,
  labelUuid = null,
  brandLogoUrl = null,
  brandDisplayName = null,
  productCategoryOptions = [],
  weightUnitOptions = [],
  useRestrictionOptions = [],
  minimumUserAgeOptions = [],
  careSymbolOptions = [],
  fibreComponentTypeOptions = [],
  fibreTypeOptions = [],
  representativeTypeOptions = [],
} = {}) => {
  const countryLabel =
    typeof resolveCountryLabel === "function"
      ? resolveCountryLabel
      : (code) => displayText(code);

  const resolveProductTypeLabel = (value) =>
    resolveGpsrOptionLabel(productCategoryOptions, value);
  const resolveRestrictionLabel = (value) =>
    resolveGpsrOptionLabel(useRestrictionOptions, value);
  const resolveAgeLabel = (value) =>
    resolveGpsrOptionLabel(minimumUserAgeOptions, value);
  const resolveCareSymbolLabel = (value) =>
    resolveGpsrOptionLabel(careSymbolOptions, value);
  const resolveComponentTypeLabel = (value) =>
    resolveGpsrOptionLabel(fibreComponentTypeOptions, value);
  const resolveFibreTypeLabel = (value) =>
    resolveGpsrOptionLabel(fibreTypeOptions, value);
  const resolveRepresentativeTypeLabel = (value) =>
    resolveGpsrOptionLabel(representativeTypeOptions, value);

  const productFields = [
    { label: "Product", value: displayText(traceability.productName) },
    { label: "Lot", value: displayText(traceability.batchLotNumber) },
    { label: "Size", value: displayText(traceability.size) },
    {
      label: "Gross Weight",
      value: (() => {
        const weight = formatPpwrWeightValue(traceability.grossWeight);
        if (!weight) return "";
        const unit = displayText(traceability.grossWeightUnit);
        if (!unit) return weight;
        return `${weight} ${unit}`;
      })(),
    },
    { label: "SKU", value: displayText(traceability.sku) },
  ].filter((item) => item.value);

  const productTypeRaw = displayText(traceability.productType);
  const productDetailFields = [
    {
      label: "Type of product",
      value: productTypeRaw
        ? resolveProductTypeLabel(productTypeRaw) || productTypeRaw
        : "",
    },
    {
      label: "Country of origin",
      value: countryLabel(traceability.countryOfOrigin),
    },
    { label: "Expiry date", value: displayText(traceability.expiryDate) },
    {
      label: "GTIN, EAN or barcode",
      value: displayText(traceability.barcode),
    },
    {
      label: "Colour or variant",
      value: displayText(traceability.colourVariant),
    },
    { label: "Model", value: displayText(traceability.model) },
    { label: "GPC Code", value: displayText(traceability.gpcCode) },
  ].filter((item) => item.value);

  const manufacturerName =
    displayText(manufacturer.businessName) ||
    displayText(manufacturer.companyName);
  const manufacturerAddress = formatPartyAddress(manufacturer, countryLabel);
  const manufacturerEmail = displayText(manufacturer.email);

  let euRepresentativeName = "";
  let euRepresentativeAddress = "";
  let euRepresentativeEmail = "";
  if (representative?.enabled) {
    if (representative.mode === "lappa") {
      euRepresentativeName =
        resolveRepresentativeTypeLabel("lappa") || "Lappa";
    } else {
      const custom = representative.custom || {};
      euRepresentativeName = displayText(custom.companyName);
      euRepresentativeAddress = formatPartyAddress(custom, countryLabel);
      euRepresentativeEmail = displayText(custom.email);
    }
  }

  const showEuRepresentative = Boolean(
    representative?.enabled &&
      (euRepresentativeName ||
        euRepresentativeAddress ||
        euRepresentativeEmail)
  );

  const importerName = displayText(importer?.companyName);
  const importerAddress = formatPartyAddress(importer || {}, countryLabel);
  const importerEmail = displayText(importer?.email);
  const showImporter = Boolean(
    importer?.enabled && (importerName || importerAddress || importerEmail)
  );

  const isTextile = isGpsrClothingTextileProductType(traceability.productType);
  const fibreComponents = isTextile
    ? (Array.isArray(fibreComposition?.components)
        ? fibreComposition.components
        : []
      )
        .map((component) => {
          const name = resolveComponentTypeLabel(component?.componentType);
          const summary = formatFibreSummary(
            component?.fibres,
            resolveFibreTypeLabel
          );
          if (!name || !summary || summary === "—") return null;
          return {
            id: component.id,
            name,
            summary,
          };
        })
        .filter(Boolean)
    : [];

  const restrictions = Array.isArray(safety?.approvedUseRestrictions)
    ? safety.approvedUseRestrictions
    : [];
  const careSymbols = isTextile
    ? (Array.isArray(safety?.careSymbols) ? safety.careSymbols : [])
        .map((value) => resolveCareSymbolLabel(value) || displayText(value))
        .filter(Boolean)
    : [];
  const additionalCareInstructions = isTextile
    ? displayText(safety?.additionalCareInstructions)
    : "";
  const showCareInstructions =
    careSymbols.length > 0 || Boolean(additionalCareInstructions);

  const safetyFields = [
    {
      label: "Safety information",
      value: displayText(safety?.safetyInformation),
    },
    {
      label: "Restrictions",
      value: restrictions
        .map((value) => resolveRestrictionLabel(value) || displayText(value))
        .filter(Boolean)
        .join(" • "),
    },
    {
      label: "Minimum age",
      value: (() => {
        const raw = displayText(safety?.minimumUserAge);
        if (!raw) return "";
        return resolveAgeLabel(raw) || raw;
      })(),
    },
    {
      label: "Temperature",
      value: (() => {
        if (!hasRestriction(restrictions, MAXIMUM_LIQUID_TEMPERATURE)) {
          return "";
        }
        const temp = displayText(safety?.maximumLiquidTemperature);
        if (!temp) return "";
        return `${temp} °C`;
      })(),
    },
    {
      label: "Warning",
      value: hasRestriction(restrictions, OTHER_WARNING)
        ? displayText(safety?.other)
        : "",
    },
  ].filter((item) => item.value);

  return {
    productFields,
    productDetailFields,
    manufacturerName,
    manufacturerAddress,
    manufacturerEmail,
    showEuRepresentative,
    euRepresentativeName,
    euRepresentativeAddress,
    euRepresentativeEmail,
    showImporter,
    importerName,
    importerAddress,
    importerEmail,
    fibreComponents,
    careSymbols,
    additionalCareInstructions,
    showCareInstructions,
    safetyFields,
    qrCodeValue: displayText(qrCodeValue) || null,
    qrCodeImage: displayText(qrCodeImage) || null,
    labelUuid: displayText(labelUuid) || null,
    brandLogoUrl: displayText(brandLogoUrl) || null,
    brandDisplayName:
      displayText(brandDisplayName) || manufacturerName || null,
    hasExtraParty: showEuRepresentative || showImporter,
    isDense:
      fibreComponents.length > 0 ||
      showCareInstructions ||
      safetyFields.length > 0 ||
      productDetailFields.length > 0,
  };
};

export const buildGpsrPrintableLabelPropsFromDetail = ({
  label,
  productSelects = null,
  resolveCountryLabel,
} = {}) => {
  if (label == null || typeof label !== "object") return null;

  const mapped = mapProductLabelDetailToGpsrForm(label, {});
  const identity = resolveGpsrProductLabelDigitalIdentity(label);
  const filePaths = resolveGpsrProductLabelFilePaths(label);
  const selects =
    productSelects && typeof productSelects === "object" ? productSelects : {};

  const brandLogoPath = displayText(filePaths.brandLogo);
  const brandLogoUrl = brandLogoPath
    ? buildInvoicePreviewUrl(brandLogoPath) || brandLogoPath
    : null;

  return buildGpsrPrintableLabelProps({
    traceability: mapped.traceability,
    manufacturer: mapped.manufacturer,
    representative: mapped.representative,
    importer: mapped.importer,
    fibreComposition: {
      components: mapGpsrFibreComponentsFromApi(label.fibre_components),
    },
    safety: mapped.safety,
    resolveCountryLabel,
    qrCodeValue: identity.qrCode,
    qrCodeImage: identity.qrCodeImage,
    labelUuid: identity.uuid,
    brandLogoUrl,
    brandDisplayName:
      displayText(mapped.manufacturer?.businessName) ||
      displayText(mapped.manufacturer?.companyName) ||
      null,
    productCategoryOptions: pickGpsrSelectOptions(
      selects.productCategories,
      FALLBACK_PRODUCT_CATEGORY_OPTIONS
    ),
    weightUnitOptions: pickGpsrSelectOptions(
      selects.weightUnits,
      FALLBACK_WEIGHT_UNIT_OPTIONS
    ),
    useRestrictionOptions: pickGpsrSelectOptions(
      selects.useRestrictions,
      FALLBACK_USE_RESTRICTION_OPTIONS
    ),
    minimumUserAgeOptions: pickGpsrSelectOptions(
      selects.minimumUserAges,
      FALLBACK_MINIMUM_USER_AGE_OPTIONS
    ),
    careSymbolOptions: pickGpsrSelectOptions(
      selects.careSymbols,
      FALLBACK_CARE_SYMBOL_OPTIONS
    ),
    fibreComponentTypeOptions: pickGpsrSelectOptions(
      selects.fibreComponentTypes,
      FALLBACK_FIBRE_COMPONENT_TYPE_OPTIONS
    ),
    fibreTypeOptions: pickGpsrSelectOptions(
      selects.fibreTypes,
      FALLBACK_FIBRE_TYPE_OPTIONS
    ),
    representativeTypeOptions: buildGpsrRepresentativeTypeOptions(
      selects.representativeTypes
    ),
  });
};
